/* eslint-disable react/jsx-pascal-case */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Condition from 'tuc-webui-base/services/Condition';
import TabPanel, { a11yProps } from './TabPanel';
import './SwipeableTabs.scss';

// Wait for https://github.com/oliviertassinari/react-swipeable-views/pull/616
// for warning in the console about componentWillReceiveProps

const TABLE_TABS_VARIANT = 'tabletabs';

const noHiddenTabs = (tabCount) => {
  const identity = [];
  for (let i = 0; i < tabCount; i += 1) {
    identity[i] = i;
  }
  return { dataToVisible: identity, visibleToData: identity, hiddenTabs: null };
};

const computeVisibleTabs = (conditions, data, tabCount, props) => {
  if (!conditions) {
    return noHiddenTabs(tabCount);
  }
  const hiddenTabs = conditions.map(c => c && data && !c(data, props));

  const dataToVisible = new Array(tabCount);
  const visibleToData = [];
  let firstHidden = -1;
  let lastHidden = -1;
  for (let i = 0; i < hiddenTabs.length; i += 1) {
    if (hiddenTabs[i]) {
      if (firstHidden === -1) {
        firstHidden = i;
      }
      lastHidden = i;
    } else {
      dataToVisible[i] = visibleToData.length;
      if (lastHidden !== -1) {
        while (firstHidden <= lastHidden) {
          dataToVisible[firstHidden] = visibleToData.length;
          firstHidden += 1;
        }
        firstHidden = -1;
        lastHidden = -1;
      }
      visibleToData.push(i);
    }
  }
  if (lastHidden !== -1) {
    while (firstHidden <= lastHidden) {
      dataToVisible[firstHidden] = visibleToData.length - 1;
      firstHidden += 1;
    }
  }
  return { dataToVisible, visibleToData, hiddenTabs };
};

const computeTabLabels = (components, setTabLabels, props) => {
  const subscribers = [];
  const tabLabels = new Array(components.length);
  const tabUpdaters = new Array(components.length);
  const makeTabUpdater = i => (newLabel) => {
    setTabLabels(components.map((tab, iTab) => {
      return (iTab === i ? newLabel : tabUpdaters[iTab](tab.label));
    }));
  };
  components.forEach((tab, iTab) => {
    const rowCountProperty = tab.rowCountProperty && props[tab.rowCountProperty];
    if (rowCountProperty) {
      tabUpdaters[iTab] = () => rowCountProperty.computeLabel(tab.label);
      tabLabels[iTab] = rowCountProperty.computeLabel(tab.label);
      subscribers.push({
        rowCountProperty,
        subsribers: rowCountProperty.subscribeSuffiexedLabel(tab.label, makeTabUpdater(iTab)),
      });
    } else {
      tabUpdaters[iTab] = label => label;
      tabLabels[iTab] = tab.label;
    }
  });
  return {
    tabLabels,
    subscribers,
  };
};

class SwipeableTabs extends React.PureComponent {
  static getDerivedStateFromProps(props, state = {}) {
    const { components, properties, viewService, data, selected } = props;
    let newState = null;
    if ((state.components !== components) || (state.properties !== properties)) {
      if (state.subscribers) {
        state.subscribers.subscribers.forEach(({ rowCountProperty, subscriber }) => {
          rowCountProperty.unsubscribe(subscriber);
        });
      }
      newState = {
        components,
        properties,
        conditions: !components || !components.find(tab => tab.if)
          ? null
          : components.map(tab => (tab.if ? Condition(tab.if, properties, viewService) : null)),
        ...computeTabLabels(components, state.setTabLabels, props),
      };
    }
    if (newState || (state.data !== data)) {
      newState = newState
        ? { ...newState, ...computeVisibleTabs(newState.conditions, data, components.length, props) }
        : computeVisibleTabs(state.conditions, data, components.length, props);
      newState.data = data;
    }
    if (state.selected !== selected) {
      newState = Object.assign(newState || {}, { selected, selectedTab: selected || 0 });
    }
    return newState;
  }

  constructor(props) {
    super(props);
    const { selected } = props;

    // Tab labels
    this.subscribers = [];
    this.state = {
      setTabLabels: tabLabels => this.setState({ tabLabels }),
      selected,
      selectedTab: selected || 0,
    };

    this.handleTabChange = this.handleTabChange.bind(this);
    this.handleChangeIndex = this.handleChangeIndex.bind(this);
  }

  componentDidMount() {
    this.mounted = true;
    this.loadLib();
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.state && this.state.subscribers) {
      this.state.subscribers.forEach(({ rowCountProperty, subscriber }) => {
        rowCountProperty.unsubscribe(subscriber);
      });
    }
  }

  async loadLib() {
    this.setState({ SwipeableViews: await import(/* webpackChunkName: "react-swipeable-views" */ 'react-swipeable-views') });
  }

  // Handle tab selection
  setTabSelection(index) {
    if (index < this.props.components.length) {
      const { visibleToData } = this.state;
      const selectedTab = visibleToData[index];
      this.setState({ selectedTab });
      if (this.props.onSelect) {
        this.props.onSelect(selectedTab);
      }
    }
  }

  handleTabChange(event, newValue) {
    this.setTabSelection(newValue);
  }

  handleChangeIndex(index) {
    this.setTabSelection(index);
  }

  render() {
    const { SwipeableViews, dataToVisible, selectedTab, hiddenTabs, tabLabels } = this.state;
    if (!SwipeableViews) {
      return <div />;
    }
    // Do not propagate the selected property to child components
    const { configuration, components, getTabLabel, style, className, selected, onSelect, ...rest } = this.props;
    const visibleSelectedTab = dataToVisible[selectedTab];
    return (
      <div className={classnames('grb-swipeable-tabs', className, configuration.fullHeight && 'grb-swipeable-tabs-full-height')} style={style}>
        <Tabs
          value={visibleSelectedTab}
          indicatorColor={configuration.indicatorColor || (configuration.variant === TABLE_TABS_VARIANT ? 'secondary' : 'primary')}
          textColor={configuration.textColor || (configuration.variant === TABLE_TABS_VARIANT ? undefined : 'primary')}
          onChange={this.handleTabChange}
          aria-label="page tabs"
          className={classnames(
            configuration.className,
            configuration.variant && `grb-${configuration.variant}`,
            // configuration.variant === SECONDARY_VARIANT && 'MuiPaper-elevation4',
          )}
        >
          {components.map((tab, iTab) => (
            <Tab
              label={getTabLabel ? getTabLabel(tab, iTab, rest) : tabLabels[iTab]}
              {...a11yProps(tab.id)}
              key={tab.id}
            />
          )).filter((x, iTab) => !hiddenTabs || !hiddenTabs[iTab])}
        </Tabs>
        <SwipeableViews.default
          className={classnames('grb-swipeable-tabs-container', configuration.fullHeight && 'MuiPaper-elevation4')}
          axis="x"
          index={visibleSelectedTab}
          onChangeIndex={this.handleChangeIndex}
        >
          {components.filter((c, iTab) => !hiddenTabs || !hiddenTabs[iTab]).map((c, iTab) => (
            <TabPanel
              {...rest}
              value={visibleSelectedTab}
              index={iTab}
              {...c}
              fullHeight={configuration.fullHeight}
              key={c.id}
            />
          ))}
        </SwipeableViews.default>
      </div>
    );
  }
}

SwipeableTabs.propTypes = {
  className: PropTypes.string,
  components: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    path: PropTypes.string,
    query: PropTypes.string,
  })).isRequired,
  configuration: PropTypes.shape({
    className: PropTypes.string,
    fullHeight: PropTypes.bool,
    variant: PropTypes.string,
    indicatorColor: PropTypes.string,
    textColor: PropTypes.string,
  }),
  style: PropTypes.shape({}),
  selected: PropTypes.number,
  onSelect: PropTypes.func,
  getTabLabel: PropTypes.func,
  properties: PropTypes.arrayOf(PropTypes.shape({})),
  viewService: PropTypes.shape({}),
  data: PropTypes.shape({}),
};

SwipeableTabs.defaultProps = {
  className: undefined,
  style: undefined,
  configuration: {},
  selected: undefined,
  onSelect: undefined,
  getTabLabel: undefined,
  properties: [],
  viewService: undefined,
  data: undefined,
};

export default SwipeableTabs;
